import React from "react";
import { useNavigate } from "react-router-dom";
import Section from "../components/Section";

const SECRET_README_CONTENT = `# Hidden Artifact: Enkae's Secret README

Welcome, code inspector. Your curiosity is noted. If you found this, you have the right mindset for my team.

![Enkae Logo](https://placehold.co/400x150/000/FFF?text=Enkae+Logo+V3.1)

## What You've Discovered

You've proven that you:
- Read source code (a rare and valuable skill)
- Pay attention to details
- Think like a developer

These are exactly the qualities I look for in collaborators.

## The Journey Continues

This portfolio is more than just a showcase—it's a living project. Every line of code tells a story, every component has a purpose, and every Easter egg rewards the curious.

Keep exploring. There's always more to discover.

---

*"The best code is not just functional—it's discoverable."*

— Enkae`;

export default function Secret() {
  const navigate = useNavigate();

  const escapeHtml = (text: string) => {
    const map: { [key: string]: string } = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#039;"
    };
    return text.replace(/[&<>"']/g, (m) => map[m]);
  };

  const renderMarkdown = (markdown: string) => {
    let escaped = escapeHtml(markdown);

    return escaped
      .replace(/^# (.*$)/gim, '<h1 class="text-lime-300 text-2xl font-bold mb-4">$1</h1>')
      .replace(/^## (.*$)/gim, '<h2 class="text-lime-200 text-xl font-bold mt-6 mb-3">$1</h2>')
      .replace(/^### (.*$)/gim, '<h3 class="text-lime-100 text-lg font-bold mt-4 mb-2">$1</h3>')
      .replace(/!\[(.*?)\]\((.*?)\)/gim, '<img src="$2" alt="$1" class="my-4 rounded-card border border-border-subtle/40" />')
      .replace(/\*\*(.*?)\*\*/gim, '<strong class="text-yellow-300">$1</strong>')
      .replace(/\*(.*?)\*/gim, '<em class="text-yellow-200">$1</em>')
      .replace(/`(.*?)`/gim, '<code class="bg-black/40 px-2 py-1 rounded text-lime-300">$1</code>')
      .replace(/^\- (.*$)/gim, '<li class="ml-4 mb-1">$1</li>')
      .replace(/\n\n/gim, '</p><p class="mb-4">')
      .replace(/^(?!<[h|l|i|s|c])/gim, '<p class="mb-4">')
      .replace(/(<li.*<\/li>)/gim, '<ul class="list-disc ml-6 mb-4">$1</ul>');
  };

  return (
    <main className="min-h-screen font-mono text-lime-100">
      <Section title="secret/README" eyebrow="hidden artifact" containerClassName="max-w-4xl">
        <div className="bg-surface/90 border border-border-subtle rounded-card shadow-card p-6 sm:p-8">
          <div className="mb-6 pb-4 border-b border-border-subtle flex items-center gap-2 text-yellow-400">
            <span className="text-green-400">$</span>
            <span>cat secret_file.md</span>
          </div>
          <div
            className="prose prose-invert max-w-none text-lime-200"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(SECRET_README_CONTENT) }}
          />

          <div className="mt-8 pt-6 border-t border-border-subtle">
            <button
              onClick={() => navigate("/projects")}
              className="btn-secondary text-lime-200 hover:text-lime-100"
            >
              $ cd ../projects
            </button>
          </div>
        </div>
      </Section>
    </main>
  );
}
